<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import { ProductCode } from '~/types'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { GuideListContext } from '~/components/molecules/important-guides.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | Third party insurance | Auto insurance for businesses',
		description:
			'Auto insurance cover that protects your vehicles against third party liabilities, death, bodily injury and property damage',
		tags: ['Auto insurance, third party', 'Car insurance', 'Third party insurance', 'Third party auto insurance'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/third-party`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
	const isDialogOpen = shallowRef(false)
	const showBenefits = shallowRef(false)
	const { navigateToProduct, viewMoreProducts } = useProductsStore()
	const benefitHighlights = [
		{
			title: 'Get covered',
			description:
				'Getting auto Insurance shouldn’t waste time. You receive your insurance certificate instantly.',
			image: '/v1727082981/ivc_l9sudc.webp',
			open: true
		},
		{
			title: 'Fast claims',
			description:
				'Claims settlement is digital, fast, seamless, and transparent powered by our A.I claims solution.',
			image: '/v1727082984/qcp_oxiunb.webp'
		},
		{
			title: 'Flexible',
			description:
				'You’ve already spent a lot on your vehicles. You shouldn’t have to pay so much to protect them.',
			image: '/v1727082981/auto-bfo_gezk97.webp'
		},
		{
			title: 'Valid insurance policies',
			description:
				'Our auto insurance policies are licensed and genuine. Once the purchase is completed, the policy is automatically uploaded on the NIID database, hence assuring verification of certificate by law enforcement officials.',
			image: '/v1727082982/inu_xwtqqi.webp'
		},
		{
			title: 'Bulk processing',
			description:
				"This feature enables you to upload your entire fleet's information in one go, saving you time and effort.",
			image: '/v1727082967/highlight-4_rr0xhn.webp'
		},
		{
			title: 'Delegate like a genius',
			description: 'Assign roles and permissions to trusted teammates. Share the control, not the stress.',
			image: '/v1727082977/oan_gqspqy.webp'
		},
		{
			title: 'Analytics and insights',
			description:
				'View transaction history from your account dashboard, including premium payments, employee utilisation, and renewals.',
			image: '/v1727082982/cph_e6lxub.webp'
		},
		{
			title: 'Employee Mobile App',
			description:
				'Protection from third-party damages just got easier! Download the MyCover app and get covered in a few taps!',
			image: '/v1761841931/mobile-app-comms_opxlgb.webp'
		}
	]
	const faqList = ref<FAQContext[]>([
		{
			question: 'What does Third-Party Auto cover?',
			answer: 'It covers third-party bodily injury (unlimited) and third-party property damage (up to ₦3,000,000).',
			open: true
		},
		{
			question: 'Does Third-Party Auto cover my own vehicle’s damages?',
			answer: 'No, it only covers damages caused to third parties, not your own vehicle.',
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'Third Party Auto',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082911/third-party_vkyrnd.webp',
						url: seo.url,
						price: '15000'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'Third Party Auto', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'auto-wishlist',
				height: '80',
				width: '80'
			},
			title: 'Vehicle details',
			description: 'Fill in the required details to register your vehicles.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'auto-card',
				height: '140',
				width: '140'
			},
			title: 'Make payment',
			description:
				'Once your vehicle details are entered, proceed to make the payment for insurance coverage and complete your purchase.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'travel-umbrella',
				height: '80',
				width: '80'
			},
			title: 'Get certificate instantly',
			description:
				'After completing the payment and activation process, receive your insurance certificate via email.'
		}
	])
	const btnClass: H1Config = {
		tag: { base: 'bg-main text-white sm:hidden' },
		wrapperClass: {
			alignItems: '',
			overflow: '',
			base: 'md:pb-10'
		},
		hClass: {
			wrapper: {
				base: 'lg:max-w-[1000px] mx-auto sm:text-center',
				text: '',
				size: 'text-[2.75rem]/[3.135rem] -tracking-[0.0825rem] md:text-[3.625rem]/[3.83rem] md:-tracking-[0.10rem] lg:text-[4.25rem]/[4.5rem] lg:-tracking-[0.135rem]'
			}
		},
		paragraphClass: {
			maxWidth: 'max-w-[634px] md:leading-[32px] sm:text-center mx-auto',
			text: 'lg:text-xl text-base md:text-lg sm:text-center'
		},
		btn: {
			base: 'md:mt-10 md:flex justify-center items-center  sm:text-center flex-col ',
			primary: {
				btn: {
					base: 'bg-white  text-base text-auto-800 hover:text-white',
					onHoverPseudo: 'hover:before:bg-auto-500'
				},
				text: {
					size: 'text-sm sm:text-base'
				}
			},
			secondary: {
				base: 'default-btn  max-w-[405px] text-center justify-center w-full gap-x-3',
				text: 'text-sm md:text-base'
			}
		}
	}
	const guideList: GuideListContext = [
		{
			tab: 'claim_process',
			label: 'Simple. Fast. Transparent.',
			description: "That's our vehicle insurance claims process in three words. Here's how it works",
			list: [
				{
					iconTag: {
						dir: 'random',
						icon: 'clipboard-text',
						height: 16
					},
					title: 'Report Claim',
					description:
						'File your claim instantly from your dashboard. No need for endless calls or paperwork.',
					imageSrc: '/v1727082821/auto-claim_hsfvml.gif',
					show: true
				},
				{
					title: 'Claims assessment',
					description: 'Your claim is quickly reviewed to ensure a smooth process.',
					iconTag: {
						dir: 'random',
						icon: 'clock'
					},
					imageSrc: '/v1727082834/auto-claim-2_rlizty.gif'
				},
				{
					title: 'Fast payout',
					description:
						"Got the green light? Enjoy speedy payouts for approved claims – because getting back on the road shouldn't be a wait.",
					iconTag: {
						dir: 'random',
						icon: 'receipt'
					},
					imageSrc: '/v1727082957/gadget-claim-3_rals23.webp'
				}
			]
		}
	]
	const teamBenefits: teamBenefitsContext = [
		{
			title: 'Third-party <br /> protection',
			description:
				'Our 3rd party auto insurance shields your business against damages caused to others on the road.',
			icon: {
				icon: 'auto-users-add',
				dir: 'random'
			}
		},
		{
			title: 'Unlimited bodily injury <br /> coverage',
			description:
				'Enjoy unlimited coverage for bodily injury to others under our 3rd party auto insurance plan.',
			icon: {
				icon: 'auto-injury',
				dir: 'random'
			}
		},
		{
			title: 'Property damage up to <br /> ₦3,000,000',
			description:
				'Our insurance covers third-party property damage up to ₦3 million, offering financial protection against unforeseen accidents.',
			icon: {
				icon: 'house-storms',
				dir: 'random'
			}
		}
	]
</script>

<template>
	<div>
		<header class="mt-0 min-h-[600px] md:min-h-[770px] bg-auto-800 px-5 md:px-0 pb-24 pt-20 xs:pt-24">
			<ui-pad :ui="{ paddingBlock: '', paddingInline: '' }" class="w-full">
				<ui-h1
					paragraph="Protect your vehicles against thrid party liabilities, for death, <br class='max-sm:hidden' /> bodily injury and property damage"
					:ui="btnClass"
					tag="Third Party"
					:tag-icon="{ dir: 'random', icon: 'bus-white' }"
					primary-btn-text="Protect your vehicle in 5 minutes <span class='max-sm:hidden'>or less</span>"
					secondary-btn-text="Speak to us to get on board"
					:action="{ type: 'onboard', product: ProductCode.THIRD_PARTY }"
				>
					<template #title="{ highlightColor }">
						Affordable coverage for your <span :class="highlightColor">business vehicles</span>
					</template>
				</ui-h1>
			</ui-pad>
		</header>
		<animated-third-party-frame
			alt="Business vehicles representing standard third-party auto insurance"
			:ui="{
				wrapper: {
					display: 'block',
					base: 'w-full justify-center max-w-[1040px] mx-auto relative -top-14 xs:-top-16 sm:-top-[150px] md:-top-[180px] xl:-top-[200px]',
					height: '',
					padding: 'px-5 sm:px-8 lg:px-0'
				},

				frame: {
					height: '',
					rounded: 'rounded-lg'
				},
				motionItem: {
					background: ''
				}
			}"
		>
			<template #image>
				<div
					class="flex gap-x-5 md:gap-x-10 sm:top-3 w-full max-sm:h-[220px] relative flex-row-reverse sm:flex-row -left-[10px] sm:-left-5 mt-auto items-stretch pt-[10px]"
				>
					<nuxt-img
						:src="'/v1727082911/third-party_vkyrnd.webp'"
						loading="lazy"
						provider="cloudinary"
						alt="smiles"
						class="snap-mandatory flex-none snap-start h-full max-h-[310px] md:max-h-[520px] max-sm:object-cover max-sm:rounded-lg"
					/>
				</div>
			</template>
			<template #animate-icon>
				<tools-icon dir="random" icon="vehicle" />
			</template>
		</animated-third-party-frame>

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-partners />
			<ui-spacer />

			<main-benefit-description
				:highlighted-benefits="benefitHighlights"
				title="Fully transparent. Completely flexible."
				class="sm:space-y-5"
				:ui="{ li: { activeClass: 'border-auto-500', activeText: 'text-auto-500' } }"
			>
				<div class="max-w-[712px] w-full text-center mx-auto flexed flex-col">
					<p class="sm:text-xl text-base text-gray-600 mt-2">
						The perfect coverage tailored to your auto insurance needs.
					</p>
				</div>
			</main-benefit-description>
			<ui-spacer />

			<main-productivity-benefits
				title="Keep others safe on the road"
				description="Your staff drives confidently with our 3rd party auto insurance, ensuring safety for others on the road."
				:team-benefits="teamBenefits"
				product-name="third party"
				:ui="{
					container: {
						btn: {
							base: 'bg-auto-500 min-h-[56px] hover:bg-auto-800 max-w-[360px] w-full',
							onHoverPseudo: 'hover:before:bg-auto-800'
						}
					}
				}"
				@trigger="showBenefits = true"
			>
				<template #hover-icon>
					<tools-icon dir="random" icon="car-wheel" width="20" height="20" />
				</template>
			</main-productivity-benefits>
			<ui-spacer />

			<lazy-main-benefit-list-display
				product-category="auto"
				:product-code="ProductCode.THIRD_PARTY"
				title="Get Covered, Get Going"
				description="Before you hit the open road, make sure your vehicles are <br /> covered with the right auto insurance"
			/>
			<ui-spacer />

			<molecules-information-hint
				category="auto"
				title="Basic protection for your <span class='text-auto-500'>vehicle needs</span>"
			/>
			<ui-spacer />

			<molecules-process-steps
				title="Convenient. Efficient. Fast."
				description="Secure your vehicles in 3 easy steps!"
				:processes="onboardingProcesses"
				:ui="{ strokeText: { strokeColor: '#FFCA99', color: 'text-auto-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-auto-500 mt-8 min-h-[56px]',
								onHoverPseudo: 'hover:before:bg-auto-800'
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct(ProductCode.THIRD_PARTY)"
					>
						<template #icon>
							<tools-icon dir="random" icon="car-wheel" width="20" height="20" />
						</template>
						<span>Get Covered Now</span>
					</ui-btn>
				</template>
			</molecules-process-steps>
		</ui-pad>
		<ui-spacer />

		<molecules-important-guides :guide-list="guideList" />
		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>
		<ui-spacer />

		<molecules-demo-card
			description=""
			title="Comprehensive coverage for your business."
			type="auto"
			@secondary-click="viewMoreProducts('auto')"
		/>

		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>
		<ui-spacer />

		<molecules-benefit-dialog
			v-model:show="showBenefits"
			:product-name="ProductCode.THIRD_PARTY"
			product-category="auto"
		/>
		<molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
